import { useEffect, useRef } from "react";
import { MENTOR_MODAL_MODE } from "../App";

function parseDate(value) {
	// Safely parse date string or Date object, return null if invalid
	if (value === null || value === undefined) return null;
	const d = value instanceof Date ? value : new Date(value);
	return isNaN(d.getTime()) ? null : d;
}

export function useMentorModalTrigger({
	userData,
	isMentorRequestEnabled,
	openMentorModal,
	updateUserMentorReminder,
	enabled = true,
	isModalOpen = false,
}) {
	// Track modal states to prevent duplicates within session
	const handledRef = useRef({
		defaultOpened: false, // DEFAULT modal shown this session
		reminderKey: null, // REMINDER timestamp key to prevent duplicates
	});

	useEffect(() => {
		// Early exit if disabled, no user, or modal already open
		if (!enabled || !userData?._id || isModalOpen) return;

		// Business conditions: feature enabled, no mentor request sent, no mentor assigned, user wants mentor
		const canShow =
			isMentorRequestEnabled &&
			!userData.user_sent_mentor_request &&
			!userData.user_mentor?._id &&
			userData.user_wants_mentor !== false;

		if (!canShow) {
			// Reset flags when conditions no longer met
			handledRef.current.defaultOpened = false;
			handledRef.current.reminderKey = null;
			return;
		}

		const remindAtRaw = userData?.user_mentor_reminder?.remind_at ?? null;

		// 1) REMINDER MODE: reminder date exists and time has passed
		if (remindAtRaw) {
			const remindAt = parseDate(remindAtRaw);
			if (!remindAt) return; // Invalid date

			const remindTime = remindAt.getTime();
			const now = Date.now();

			// Reminder time not reached yet — wait silently
			if (now < remindTime) return;

			// Prevent duplicate REMINDER for same timestamp
			const key = String(remindTime);
			if (handledRef.current.reminderKey === key) return;
			handledRef.current.reminderKey = key;

			// Show REMINDER modal and clear reminder
			openMentorModal?.(MENTOR_MODAL_MODE.REMINDER);
			updateUserMentorReminder?.({ remind_at: null });

			// Block DEFAULT mode for rest of session after REMINDER
			handledRef.current.defaultOpened = true;
			return;
		}

		// 2) DEFAULT MODE: no reminder date set
		if (remindAtRaw === null) {
			// Show DEFAULT only once per session
			if (handledRef.current.defaultOpened) return;
			handledRef.current.defaultOpened = true;

			openMentorModal?.(MENTOR_MODAL_MODE.DEFAULT);
		}
	}, [
		enabled,
		isModalOpen,
		userData,
		isMentorRequestEnabled,
		openMentorModal,
		updateUserMentorReminder,
	]);
}
